import axios from 'axios';
import React, { useState, useRef } from 'react';
import ReCAPTCHA from '../components/ReCAPTCHA';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Film, Camera } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import '../styles/login.css';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';

const SPONSOR_API_URL = "https://pilocoustomersupport.onrender.com";

// Resolves where to send the user after a successful login. If a sponsor
// banner sent them here while logged out (AdBanner.jsx sets this key before
// navigating to /login), honor that instead of the backend's normal
// redirect_url. Mirrors the exact set-once/read-once/clear lifecycle this
// file already uses for `google_user_data`.
const resolvePendingSponsorRedirect = async (defaultRedirectUrl, accessToken) => {
  const raw = localStorage.getItem('pending_sponsor_redirect');
  if (!raw) return defaultRedirectUrl;

  localStorage.removeItem('pending_sponsor_redirect');

  let pending;
  try {
    pending = JSON.parse(raw);
  } catch (_) {
    return defaultRedirectUrl;
  }

  if (!pending?.slug || !pending?.expiry || Date.now() > pending.expiry) {
    // Expired or malformed - don't silently redirect somewhere stale.
    return defaultRedirectUrl;
  }

  try {
    const userIdRes = await fetch(`${baseUrl}/getUserIDByToken`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const { user_id } = await userIdRes.json();

    const clickRes = await fetch(`${SPONSOR_API_URL}/sponsors/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sponsor_slug: pending.slug, pb_user_id: user_id }),
    });
    if (!clickRes.ok) return defaultRedirectUrl;

    const { destination_url } = await clickRes.json();
    return destination_url || defaultRedirectUrl;
  } catch (_) {
    return defaultRedirectUrl;
  }
};

class TranslationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch() { setTimeout(() => this.setState({ hasError: false }), 100); }
  render() { return this.props.children; }
}

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [formErrors, setFormErrors] = useState({ username: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // ── Retry state ──────────────────────────────────────────────────────────
  const [retryCountdown, setRetryCountdown] = useState(null);
  const retryTimerRef = useRef(null);
  const countdownRef = useRef(null);
  // ─────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  // ── Retry logic: called on 504 / network timeout ─────────────────────────
  const scheduleRetry = (loginFn) => {
    let seconds = 90;
    setRetryCountdown(seconds);
    setIsLoading(true);
    setErrorMessage('');

    countdownRef.current = setInterval(() => {
      seconds -= 1;
      setRetryCountdown(seconds);
      if (seconds <= 0) clearInterval(countdownRef.current);
    }, 1000);

    retryTimerRef.current = setTimeout(() => {
      clearInterval(countdownRef.current);
      setRetryCountdown(null);
      loginFn();
    }, 90000);
  };

  const cancelRetry = () => {
    clearTimeout(retryTimerRef.current);
    clearInterval(countdownRef.current);
    setRetryCountdown(null);
    setIsLoading(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const attemptLogin = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/login`, new URLSearchParams({
        username,
        password
      }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 15000
      });
      const { access_token, redirect_url } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_dashboard', redirect_url);
      const finalRedirect = await resolvePendingSponsorRedirect(redirect_url, access_token);
      navigate(finalRedirect);
    } catch (error) {
      const status = error.response?.status;
      const isTimeout = error.code === 'ECONNABORTED' || status === 504 || status === 502 || status === 503;

      if (isTimeout) {
        scheduleRetry(attemptLogin);
      } else {
        setErrorMessage(
          status === 401
            ? "Invalid credentials. Please try again."
            : "An error occurred during login. Please try again later."
        );
        setIsLoading(false);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormErrors({ username: '', password: '' });

    if (!username) setFormErrors(prev => ({ ...prev, username: 'Username is required' }));
    if (!password) setFormErrors(prev => ({ ...prev, password: 'Password is required' }));
    if (!captchaValue) { setErrorMessage("Please complete the CAPTCHA."); return; }
    if (username && password && captchaValue) {
      cancelRetry();
      await attemptLogin();
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const response = await axios.post(`${baseUrl}/auth/google`, {
        token: credentialResponse.credential,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        google_id: decoded.sub
      });
      const { access_token, redirect_url, user } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_dashboard', redirect_url);
      if (!user.profile_completed) {
        localStorage.setItem('google_user_data', JSON.stringify({
          email: user.email, name: user.name, picture: user.picture
        }));
        // Profile completion is mandatory - don't let a pending sponsor
        // redirect hijack it. pending_sponsor_redirect (if any) is left in
        // localStorage and will be honored on this user's next real login,
        // as long as it hasn't hit its 10-minute expiry by then.
        navigate(redirect_url);
        return;
      }
      const finalRedirect = await resolvePendingSponsorRedirect(redirect_url, access_token);
      navigate(finalRedirect);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => { setErrorMessage('Google login failed. Please try again.'); };

  const filmReels = [...Array(20)].map((_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    rotation: Math.random() * 360,
  }));

  return (
    <TranslationErrorBoundary>
      <>
        <SEO
          title="Log In"
          description="Log in to your ProducerBuddy account to access your film production tools and projects."
          url="https://producerbuddy.pilomation.com/login"
          robots="noindex, nofollow"
        />
        <Nav />
        <div className="min-h-screen flex items-center justify-center p-8 relative bg-gray-700">
          <div className="absolute inset-0 overflow-hidden opacity-10">
            {filmReels.map((reel, i) => (
              <div key={i} className="absolute animate-pulse"
                style={{ left: reel.left, top: reel.top, transform: `rotate(${reel.rotation}deg)` }}>
                <Film className="w-16 h-16 text-cyan-300" />
              </div>
            ))}
          </div>

          <div className="pb-login-container relative max-w-2xl w-full">
            <div className="pb-login-card bg-gray-800 p-12 rounded-lg shadow-xl border border-cyan-300/30">
              <div className="text-center mb-12">
                <h1 className="pb-login-title text-3xl font-bold text-white">Producer Buddy</h1>
                <p className="pb-login-subtitle text-xl text-cyan-200 mt-2">Unlock AI-driven production excellence✨</p>
              </div>

              {/* ── Retry banner (replaces error during cold start) ── */}
              {errorMessage && retryCountdown === null && (
                <div className="pb-error-message bg-red-800 text-red-200 p-4 rounded mb-4 border border-red-400">
                  {errorMessage}
                </div>
              )}

              <form className="pb-login-form space-y-6" onSubmit={handleLogin} translate="no">
                <div className="pb-form-group">
                  <div className="pb-input-wrappers">
                    <Mail className="pb-input-icon text-cyan-300" size={20} />
                    <input type="text" id="username" placeholder="Email Address / Username"
                      value={username} onChange={(e) => setUsername(e.target.value)}
                      className={`bg-gray-800 text-white border-cyan-400/30 focus:border-cyan-300 text-lg py-3 ${formErrors.username ? 'pb-input-error' : ''}`} />
                  </div>
                  {formErrors.username && <span className="pb-error-text text-red-400">{formErrors.username}</span>}
                </div>

                <div className="pb-form-group">
                  <div className="pb-input-wrappers">
                    <Lock className="pb-input-icon text-cyan-300" size={20} />
                    <input type={showPassword ? 'text' : 'password'} id="password" placeholder="Password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className={`bg-gray-800 text-white border-cyan-400/30 focus:border-cyan-300 text-lg py-3 ${formErrors.password ? 'pb-input-error' : ''}`} />
                    <button type="button" className="pb-password-toggle text-cyan-300 hover:text-cyan-200"
                      onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formErrors.password && <span className="pb-error-text text-red-400">{formErrors.password}</span>}
                </div>

                <div className="pb-captcha-container flex justify-center">
                  <ReCAPTCHA onChange={(value) => setCaptchaValue(value)} theme="dark" />
                </div>

                <button type="submit"
                  className="pb-login-button bg-cyan-500 hover:bg-cyan-400 flex items-center justify-center gap-3 text-lg py-4 text-white font-semibold"
                  disabled={isLoading}>
                  <Camera className="w-6 h-6" />
                  {isLoading ? 'Loading...' : 'Login'}
                </button>

                <div className="pb-form-footer mt-4 text-center">
                  <Link to="/forgot-password" className="pb-forgot-link text-cyan-300 hover:text-cyan-200 text-lg">
                    Lost Your Ticket? (Reset Password)
                  </Link>
                </div>
              </form>

              <div className="pb-divider my-8">
                <span className="bg-gray-800 text-teal-400 text-lg px-4">OR</span>
              </div>

              <div className="pb-social-buttons space-y-4">
                <div className="flex justify-center">
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError}
                    useOneTap={false} theme="filled_black" size="large"
                    text="continue_with" shape="rectangular" width="100%" />
                </div>
              </div>

              <p className="pb-signup-text text-gray-400 text-lg mt-8 text-center">
                First time on set?{' '}
                <Link to="/signup" className="pb-signup-link text-cyan-300 hover:text-cyan-200">
                  Get your premiere ticket (Sign Up)<ArrowRight size={18} className="pb-icon-inline" />
                </Link>
              </p>
              <p className="text-gray-500 text-sm mt-3 text-center">
                Student at a partnered institute?{' '}
                <Link to="/student-program" className="text-teal-400 hover:text-teal-300">
                  Explore the Student Program
                </Link>
              </p>
            </div>
          </div>
        </div>
      </>
    </TranslationErrorBoundary>
  );
}

export default Login;